import Decimal from 'decimal.js';

import { ActionButtonRenderer } from '@/components/tables/DataTable/renderers/cell/ActionButtonRenderer';
import { TextRenderer } from '@/components/tables/DataTable/renderers/cell/TextRenderer';
import { TwoLineTextRenderer } from '@/components/tables/DataTable/renderers/cell/TwoLineTextRenderer';
import { Column } from '@/components/tables/DataTable/types';
import { EMPTY_CONTENT_HYPHEN } from '@/components/typography/placeholders';
import { IntegrationLinkedIcon } from '@/modules/assetProviderIntegrations/shared/IntegrationLinkedIcon';
import { useCanEditHouseholdData } from '@/modules/authentication/hooks/useCanEditHouseholdData';
import {
  LiabilityPaymentFrequency,
  LiabilityPaymentKind,
} from '@/types/schema';
import { UnreachableError } from '@/utils/errors';
import { formatCurrencyNoDecimals } from '@/utils/formatting/currency';
import { formatPercent } from '@/utils/formatting/percent';

import { DeleteLiabilityMenuItem } from '../components/DeleteLiabilityMenuItem';
import { NO_LIABILITY_SOURCE_RECIPIENT_VALUE_DISPLAY } from '../LiabilityModal/LiabilityModal.constants';
import { LiabilitiesTable_LiabilityFragment } from './graphql/LiabilitiesTable.generated';
import {
  LiabilitiesTableColumns,
  LiabilitiesTableRowData,
} from './LiabilitiesTable.constants';

export function mapLiabilitiesToRows(
  liabilities: LiabilitiesTable_LiabilityFragment[],
  householdId: string
): LiabilitiesTableRowData[] {
  return (
    liabilities
      .map((liability) => {
        const {
          recurringPaymentAmount,
          interestRate,
          paymentFrequency,
          paymentKind,
          termStart,
          termEnd,
          finalPaymentAmount,
        } = liability;
        const issuerName =
          liability.sourceClientOrganization?.name ??
          liability.sourceClientProfile?.displayName ??
          liability.sourceEntity?.subtype?.displayName ??
          NO_LIABILITY_SOURCE_RECIPIENT_VALUE_DISPLAY;

        const recipientName =
          liability.recipientEntity?.subtype?.displayName ??
          NO_LIABILITY_SOURCE_RECIPIENT_VALUE_DISPLAY;

        let recurringPaymentAmountDisplay: string = EMPTY_CONTENT_HYPHEN;
        if (recurringPaymentAmount) {
          recurringPaymentAmountDisplay = `${formatCurrencyNoDecimals(recurringPaymentAmount)}`;
          if (interestRate) {
            recurringPaymentAmountDisplay = `${recurringPaymentAmountDisplay} (${formatPercent(interestRate, 2)}% interest)`;
          }
        }

        let paymentFrequencyDisplay: string | undefined = undefined;
        if (paymentFrequency && termStart && termEnd) {
          let frequency: string;
          switch (paymentFrequency) {
            case LiabilityPaymentFrequency.Monthly:
              frequency = 'Monthly';
              break;
            case LiabilityPaymentFrequency.Annually:
              frequency = 'Annually';
              break;
            case LiabilityPaymentFrequency.Quarterly:
              frequency = 'Quarterly';
              break;
            case LiabilityPaymentFrequency.Semiannually:
              frequency = 'Semi-annually';
              break;
            default:
              throw new UnreachableError({
                case: paymentFrequency,
                message: 'Invalid payment frequency',
              });
          }
          paymentFrequencyDisplay = `${frequency} from ${termStart.getFullYear()} through ${termEnd.getFullYear()}`;
        }

        let paymentKindDisplay: string = EMPTY_CONTENT_HYPHEN;
        if (paymentKind) {
          switch (paymentKind) {
            case LiabilityPaymentKind.InterestOnly:
              paymentKindDisplay = 'Balloon payment';
              break;
            case LiabilityPaymentKind.Amortized:
              paymentKindDisplay = 'Amortizing';
              break;
            default:
              throw new UnreachableError({
                case: paymentKind,
                message: 'Invalid payment kind',
              });
          }
        }

        let finalPaymentAmountDisplay: string | undefined = undefined;
        if (
          paymentKind === LiabilityPaymentKind.InterestOnly &&
          finalPaymentAmount
        ) {
          finalPaymentAmountDisplay = `${formatCurrencyNoDecimals(finalPaymentAmount)}`;
        }

        return {
          id: liability.id,
          name: liability.displayName,
          notes: liability.notes || '',
          currentValue: liability.currentAmount ?? new Decimal(0),
          issuerName,
          recipientName,
          householdId,
          isIntegrationLinked: !!liability.integrationLiability,
          recurringPaymentAmount: liability.recurringPaymentAmount,
          interestRate: liability.interestRate,
          paymentFrequency: liability.paymentFrequency,
          paymentKind: liability.paymentKind,
          termStart: liability.termStart,
          termEnd: liability.termEnd,
          finalPaymentAmount: liability.finalPaymentAmount,

          currentValueDisplay: liability.currentAmount
            ? `${formatCurrencyNoDecimals(liability.currentAmount)} (current value)`
            : undefined,

          lenderDisplay: issuerName,
          issuerNameDisplay: `Lent from ${issuerName}`,
          recurringPaymentAmountDisplay,
          paymentFrequencyDisplay,
          paymentKindDisplay,
          finalPaymentAmountDisplay,
        };
      })
      // sort so the most negative values are at the top. the right thing to do here is to make the value field
      // sortable via the API, but it's
      .sort((a, b) => b.currentValue.minus(a.currentValue).toNumber())
  );
}

export interface UseColumnsOptions {
  refetch: () => void;
  hidePaymentColumns?: boolean;
}

export function useColumns({
  refetch,
  hidePaymentColumns = true,
}: UseColumnsOptions): Column<LiabilitiesTableRowData>[] {
  const canEdit = useCanEditHouseholdData();

  const columns: Column<LiabilitiesTableRowData>[] = [
    {
      field: LiabilitiesTableColumns.NAME,
      headerName: 'Name',
      width: 175,
      flex: 1,
      renderCell: TextRenderer({
        text: ({ row: { name } }) => name,
        textAccompaniment: ({ row }) =>
          row.isIntegrationLinked ? <IntegrationLinkedIcon /> : null,
      }),
    },
    {
      field: LiabilitiesTableColumns.LENDER,
      headerName: 'Lender',
      width: 175,
      flex: 1,
      renderCell: TextRenderer({
        text: ({ row: { lenderDisplay } }) =>
          lenderDisplay || EMPTY_CONTENT_HYPHEN,
      }),
    },
    {
      field: LiabilitiesTableColumns.DEBTOR,
      headerName: 'Debtor',
      width: 175,
      flex: 1,
      renderCell: TextRenderer({
        text: ({ row: { recipientName } }) => recipientName,
      }),
    },
    ...(!hidePaymentColumns
      ? [
          {
            field: LiabilitiesTableColumns.PAYMENT_DETAILS,
            headerName: 'Payment details',
            width: 200,
            flex: 1,
            renderCell: TwoLineTextRenderer({
              lineOne: ({ row: { recurringPaymentAmountDisplay } }) =>
                recurringPaymentAmountDisplay,
              lineTwo: ({ row: { paymentFrequencyDisplay } }) =>
                paymentFrequencyDisplay,
            }),
          },
          {
            field: LiabilitiesTableColumns.PAYMENT_KIND,
            headerName: 'Payment type',
            width: 200,
            flex: 1,
            renderCell: TwoLineTextRenderer({
              lineOne: ({ row: { paymentKindDisplay } }) => paymentKindDisplay,
              lineTwo: ({ row: { finalPaymentAmountDisplay } }) =>
                finalPaymentAmountDisplay,
            }),
          },
        ]
      : []),
  ];

  // Add outstanding balance as second to last column
  columns.push({
    field: LiabilitiesTableColumns.OUTSTANDING_BALANCE,
    headerName: 'Outstanding balance',
    width: 175,
    flex: 1,
    renderCell: TextRenderer({
      text: ({ row: { currentValue } }) =>
        formatCurrencyNoDecimals(currentValue),
    }),
  });

  if (canEdit) {
    columns.push({
      field: LiabilitiesTableColumns.ACTIONS,
      headerName: '',
      align: 'center',
      width: 64,
      minWidth: 64,
      maxWidth: 64,
      renderCell: ActionButtonRenderer({
        actions: ({ row }) => [
          {
            MenuItem: () => (
              <DeleteLiabilityMenuItem
                liabilityId={row.id}
                onDelete={refetch}
              />
            ),
          },
        ],
      }),
      sortable: false,
    });
  }

  return columns;
}
